import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db8ct58kk00l6eltripg";

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("company_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      const isSuccess = response.ok && parsed?.code === "OK";
      const spamLike =
        typeof serverMsg === "string" &&
        serverMsg.toLowerCase().includes("spam");

      if (!isSuccess || spamLike) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络连接失败，请检查网络后稍后重试。");
    }
  };

  return (
    <form
      id="db8ct58kk00l6eltripg"
      data-readdy-form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
            您的姓名
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入您的姓名"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
            电子邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="请输入常用邮箱"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-800">
          留言主题
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          placeholder="例如：内容纠错 / 合作咨询"
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请输入您想对肮脏的交易说的话（最多 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
        />
      </div>

      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="contact-form-notes"
      />

      {status === "error" && formError && (
        <p className="flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2.5 text-sm text-accent-700">
          <i className="ri-error-warning-line" />
          {formError}
        </p>
      )}

      {status === "success" && (
        <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line" />
          留言已提交成功，感谢你的反馈，我们会尽快与你联系。
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="flex items-center justify-center gap-2 rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-send-plane-line text-lg" />
        {status === "submitting" ? "正在提交..." : "提交留言"}
      </button>
    </form>
  );
}